import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { Alert, Box, Button, Grid, TextField, Typography } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import Loader from 'components/shared/loader/loader.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { getUserByIdTemplate, saveLabDentistNoteTemplate } from 'apollo/templates/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { User } from 'models/user';

type DentistNoteDialogProps = {
    open: boolean;
    onClose: () => void;
    dentistId: string;
    clinicId: string;
    clinicName?: string;
};

/**
 * DD-3468: lets a Dental Technician without client-directory access read a
 * dentist's details and edit the lab's internal note straight from the order.
 * The note is keyed on the order's customer clinic, the same id the client
 * directory uses.
 */
const DentistNoteDialog: FC<DentistNoteDialogProps> = ({
    open,
    onClose,
    dentistId,
    clinicId,
    clinicName,
}: DentistNoteDialogProps) => {
    const { t: translate } = useTranslation(['common', 'users', 'client-directory']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const lab = currentOrgVar();
    const [note, setNote] = useState('');

    const { data: { userById: dentist } = {}, loading } = useQuery<{ userById: User }>(getUserByIdTemplate, {
        skip: !open,
        fetchPolicy: 'network-only',
        variables: { userId: dentistId },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        const labNote = dentist?.dentistNotes?.find((entry) => entry.lab === lab._id && entry.clinic === clinicId);
        setNote(labNote?.note ?? '');
    }, [dentist, clinicId, lab._id]);

    const [saveLabDentistNote, { loading: saving }] = useMutation(saveLabDentistNoteTemplate, {
        onCompleted: () => {
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('common:CHANGES_SAVED') });
            onClose();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
        setNote(event.target.value);
    };

    const handleSave = async () => {
        await saveLabDentistNote({ variables: { dentistId, clinicId, note } });
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={onClose}
            title={translate('client-directory:DENTIST_DETAILS')}
            actions={
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading || saving || !dentist}
                    data-test="dentist-note-dialog.save-btn"
                >
                    {translate('common:SAVE')}
                </Button>
            }
        >
            {loading || !dentist ? (
                <Loader />
            ) : (
                <Box mt={1}>
                    <Typography variant="h6" gutterBottom data-test="dentist-note-dialog.dentist-name">
                        {dentist.fullName}
                    </Typography>
                    <Grid container spacing={2} mb={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">
                                {translate('common:EMAIL')}
                            </Typography>
                            <Typography>{dentist.email || '-'}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">
                                {translate('common:PHONE')}
                            </Typography>
                            <Typography>{dentist.phone || '-'}</Typography>
                        </Grid>
                        {clinicName && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="textSecondary">
                                    {translate('common:CUSTOMER')}
                                </Typography>
                                <Typography>{clinicName}</Typography>
                            </Grid>
                        )}
                    </Grid>
                    <TextField
                        id="dentist-note"
                        label={translate('users:INTERNAL_DENTIST_NOTE')}
                        multiline
                        minRows={4}
                        maxRows={10}
                        value={note}
                        onChange={handleNoteChange}
                        fullWidth
                        data-test="dentist-note-dialog.note-input"
                    />
                    <Box mt={1}>
                        <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                            {translate('users:DENTIST_NOTE_INFO')}
                        </Alert>
                    </Box>
                </Box>
            )}
        </DHDialog>
    );
};

export default DentistNoteDialog;
